import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, Copy, KeySquare, Plus, Trash2 } from 'lucide-react';
import {
  HowToUseParagraph,
  HowToUseSection,
  HowToUseStep,
  HowToUseSteps,
} from '@reflex/ui/components/integration/HowToUse';
import {
  getListPersonalApiKeysQueryKey,
  useCreatePersonalApiKey,
  useListPersonalApiKeys,
  useRevokePersonalApiKey,
  type ListPersonalApiKeysQueryResult,
} from '@reflex/ui/client/generated/me';
import { formatDateOnly } from '@reflex/ui/lib/format';
import { useCopyToClipboard } from '@reflex/ui/hooks/useCopyToClipboard';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@reflex/ui/components/ui/alert-dialog';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { ErrorState } from '@reflex/ui/components/shared/ErrorState';

type PersonalApiKey = ListPersonalApiKeysQueryResult['keys'][number];

/**
 * "API keys" — the personal API key surface, mounted in the Security drill
 * at `/{slug}/security/api-keys` and backed by the `/me/api-keys` API.
 *
 * Keys authenticate as the signed-in user against the public Reflex API and
 * are the credential the `@runloop/reflex-client` SDK expects. The full
 * token is returned exactly once at creation; after that only the
 * `tokenPrefix` is visible, so the create flow hands off to a one-time
 * reveal dialog with a copy affordance.
 */
export function PersonalApiKeysPage({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient();
  const listQueryKey = getListPersonalApiKeysQueryKey();

  const keysQuery = useListPersonalApiKeys();
  const keys = keysQuery.data?.keys ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<PersonalApiKey | null>(null);

  const createMutation = useCreatePersonalApiKey({
    mutation: {
      onSuccess: (result) => {
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
        toast.success('API key created');
        setCreateOpen(false);
        setCreatedToken(result.token);
      },
      onError: (err) =>
        toast.error('Failed to create API key', {
          description: err instanceof Error ? err.message : undefined,
        }),
    },
  });

  const revokeMutation = useRevokePersonalApiKey({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
        toast.success('API key revoked');
      },
      onError: (err) =>
        toast.error('Failed to revoke API key', {
          description: err instanceof Error ? err.message : undefined,
        }),
    },
  });

  const isPending = createMutation.isPending || revokeMutation.isPending;

  const body = (
    <>
      {!embedded && (
        <PageHeader
          iconVariant="box"
          icon={<KeySquare className="h-5 w-5 text-muted-foreground" />}
          title="API keys"
          description="Personal keys that call the Reflex API as you. Use them with the @runloop/reflex-client SDK or any HTTP client."
          actions={
            <Button
              aria-label="Create API key"
              disabled={isPending}
              onClick={() => setCreateOpen(true)}
            >
              <Plus />
              <span className="sm:hidden">Create</span>
              <span className="hidden sm:inline">Create API key</span>
            </Button>
          }
          howToUse={{
            label: 'How API keys work',
            children: (
              <>
                <HowToUseSection title="What they are">
                  <HowToUseParagraph>
                    Personal tokens that authenticate HTTP requests to the Reflex API as you. Use
                    them in scripts, CI jobs, or with the @runloop/reflex-client SDK.
                  </HowToUseParagraph>
                </HowToUseSection>
                <HowToUseSection title="Token visibility">
                  <HowToUseSteps>
                    <HowToUseStep>
                      The full token is shown exactly once when you create a key. Copy it
                      immediately and store it somewhere safe.
                    </HowToUseStep>
                    <HowToUseStep>
                      After creation, only the token prefix is visible on this page. Revoke a key if
                      you lose access to the full token.
                    </HowToUseStep>
                  </HowToUseSteps>
                </HowToUseSection>
              </>
            ),
          }}
        />
      )}

      {keysQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading API keys…</p>
      ) : (
        <SectionCard
          title="Personal API keys"
          description="Each token is shown once at creation. After that, only the token prefix is visible here."
          action={
            embedded ? (
              <Button
                size="sm"
                aria-label="Create API key"
                disabled={isPending}
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="size-3.5" />
                <span className="sm:hidden">Create</span>
                <span className="hidden sm:inline">Create API key</span>
              </Button>
            ) : undefined
          }
          contentClassName={keys.length > 0 ? 'divide-y py-0' : 'py-0'}
          className="gap-2 py-4 sm:gap-3"
          data-testid="personal-api-keys"
        >
          {keysQuery.isError ? (
            <ErrorState message="Failed to load API keys." />
          ) : keys.length > 0 ? (
            keys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between gap-3 py-2.5"
                data-testid={`api-key-row-${key.id}`}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-medium">{key.name}</span>
                  <p className="truncate text-xs text-muted-foreground">
                    <span className="font-mono">{key.tokenPrefix}...</span>
                    <span className="mx-1.5">·</span>
                    Created {formatDateOnly(key.createdAt)}
                    <span className="mx-1.5">·</span>
                    Last used {key.lastUsedAt === null ? 'Never' : formatDateOnly(key.lastUsedAt)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => setRevoking(key)}
                  disabled={isPending}
                  aria-label={`Revoke ${key.name}`}
                >
                  <Trash2 className="size-3" />
                </Button>
              </div>
            ))
          ) : (
            <EmptyState title="No API keys yet. Create one to call the Reflex API from scripts, CI, or the SDK." />
          )}
        </SectionCard>
      )}

      <CreateApiKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        isPending={createMutation.isPending}
        onSubmit={(name) => createMutation.mutate({ data: { name } })}
      />

      <TokenRevealDialog token={createdToken} onClose={() => setCreatedToken(null)} />

      <AlertDialog
        open={revoking !== null}
        onOpenChange={(next) => {
          if (!next) setRevoking(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
            <AlertDialogDescription>
              {revoking?.name} will be permanently revoked. Any application using this key loses
              access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (revoking) revokeMutation.mutate({ id: revoking.id });
                setRevoking(null);
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  if (embedded) return body;

  return <PageLayout maxWidth="3xl">{body}</PageLayout>;
}

interface CreateApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onSubmit: (name: string) => void;
}

/** Single-field create dialog: name the key, submit, hand off to the reveal dialog. */
function CreateApiKeyDialog({ open, onOpenChange, isPending, onSubmit }: CreateApiKeyDialogProps) {
  const [name, setName] = useState('');

  function handleOpenChange(next: boolean) {
    if (!next) setName('');
    onOpenChange(next);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || isPending) return;
    onSubmit(trimmed);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>
              Name the key after where you will use it, like a script, CI job, or app.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-2">
            <Label htmlFor="api-key-name">Name</Label>
            <Input
              id="api-key-name"
              placeholder="e.g. deploy-script"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              maxLength={100}
            />
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isPending}>
              {isPending ? 'Creating…' : 'Create key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface TokenRevealDialogProps {
  /** Full token to reveal, or null when the dialog is closed. */
  token: string | null;
  onClose: () => void;
}

/** One-time token reveal: the full token is only ever shown in this dialog. */
function TokenRevealDialog({ token, onClose }: TokenRevealDialogProps) {
  const { copied, copy } = useCopyToClipboard();

  return (
    <Dialog
      open={token !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>API key created</DialogTitle>
          <DialogDescription>
            Copy the token now and store it somewhere safe. You will not be able to see this token
            again.
          </DialogDescription>
        </DialogHeader>
        <code
          className="mt-2 block select-all break-all rounded-md border bg-muted/60 p-3 font-mono text-sm"
          data-testid="api-key-token"
        >
          {token}
        </code>
        <DialogFooter className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (token) copy(token);
            }}
            aria-label={copied ? 'Copied token' : 'Copy token'}
          >
            {copied ? (
              <>
                <Check className="icon-sm text-status-success" /> Copied
              </>
            ) : (
              <>
                <Copy className="icon-sm" /> Copy token
              </>
            )}
          </Button>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
